import { useState, useEffect, useRef } from 'react'
import QRCode from 'qrcode.react'
import { useMenuData } from '@/hooks/useMenuData'
import { useLanguage } from '@/hooks/useLanguage'
import { ThemeProvider } from '@/components/common/ThemeProvider'

// ============================================================================
// QR Code Page — generates menu QR codes, optionally per-table.
// ============================================================================

type TableSelection = 'none' | number

export function QRPage() {
  const { config, isLoading } = useMenuData()
  const { language } = useLanguage()
  const qrRef = useRef<HTMLDivElement>(null)
  const [imageUrl, setImageUrl] = useState<string>('')

  const [tableCount, setTableCount] = useState(12)
  const [selectedTable, setSelectedTable] = useState<TableSelection>('none')

  // Base menu URL (no hash — we never want the /qr route baked into the code)
  const baseUrl =
    typeof window !== 'undefined'
      ? window.location.origin + window.location.pathname
      : 'https://omnirise.cafe'

  const menuUrl =
    selectedTable === 'none' ? baseUrl : `${baseUrl}?table=${selectedTable}`

  // (Re)generate the downloadable PNG whenever the QR content changes
  useEffect(() => {
    if (qrRef.current) {
      const canvas = qrRef.current.querySelector('canvas')
      if (canvas) setImageUrl(canvas.toDataURL('image/png'))
    }
  }, [config, menuUrl])

  const downloadQR = () => {
    if (!imageUrl) return
    const slug = config?.business.name?.toLowerCase().replace(/\s+/g, '-') || 'menu'
    const suffix = selectedTable === 'none' ? '' : `-table-${selectedTable}`
    const link = document.createElement('a')
    link.href = imageUrl
    link.download = `qr-${slug}${suffix}.png`
    link.click()
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!config) return null

  // i18n strings
  const t = {
    scan: language === 'fr' ? 'Scannez pour voir le menu' : language === 'ar' ? 'امسح لرؤية القائمة' : 'Scan to view menu',
    download: language === 'fr' ? 'Télécharger le QR Code' : language === 'ar' ? 'تنزيل رمز الاستجابة السريعة' : 'Download QR Code',
    print: language === 'fr' ? 'Imprimer' : language === 'ar' ? 'طباعة' : 'Print',
    place: language === 'fr' ? 'Placez ce QR code sur vos tables' : language === 'ar' ? 'ضع رمز الاستجابة السريعة هذا على طاولاتك' : 'Place this QR code on your tables',
    table: language === 'fr' ? 'Table' : language === 'ar' ? 'طاولة' : 'Table',
    noTable: language === 'fr' ? 'Aucune (menu général)' : language === 'ar' ? 'بدون (قائمة عامة)' : 'None (general menu)',
    tableCountLabel: language === 'fr' ? 'Nombre de tables' : language === 'ar' ? 'عدد الطاولات' : 'Number of tables',
    tableHelp:
      selectedTable === 'none'
        ? language === 'fr'
          ? "Le QR mène au menu sans numéro de table."
          : language === 'ar'
          ? 'الرمز يؤدي إلى القائمة بدون رقم طاولة.'
          : 'The QR opens the menu with no table number.'
        : language === 'fr'
        ? `Le QR inclut ?table=${selectedTable} — la commande WhatsApp indiquera « Table ${selectedTable} ».`
        : language === 'ar'
        ? `الرمز يتضمن ?table=${selectedTable} — طلب واتساب سيُظهر « طاولة ${selectedTable} ».`
        : `The QR includes ?table=${selectedTable} — the WhatsApp order will say “Table ${selectedTable}”.`,
  }

  return (
    <ThemeProvider theme={config.theme}>
      <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-8">
        <div className="max-w-md w-full bg-white rounded-card shadow-lg p-6 sm:p-8 text-center">
          {/* Logo + Name */}
          {config.business.logo && (
            <img
              src={config.business.logo}
              alt={config.business.name}
              className="w-20 h-20 mx-auto mb-4 rounded-full object-cover"
            />
          )}
          <h1 className="text-2xl font-bold text-primary mb-1">{config.business.name}</h1>
          {config.business.tagline && (
            <p className="text-sm text-muted-foreground mb-6">{config.business.tagline}</p>
          )}

          {/* QR Code */}
          <div ref={qrRef} className="flex justify-center mb-4">
            <QRCode value={menuUrl} size={240} level="M" includeMargin bgColor="#FFFFFF" fgColor="#000000" />
          </div>

          <p className="text-base font-semibold mb-1">{t.scan}</p>
          <p className="text-xs text-muted-foreground break-all mb-5">{menuUrl}</p>

          {/* Table controls */}
          <div className="bg-secondary/30 rounded-xl p-4 mb-5 text-left space-y-3">
            <div className="flex items-center justify-between gap-3">
              <label htmlFor="qr-table" className="text-sm font-medium text-primary shrink-0">
                {t.table}
              </label>
              <select
                id="qr-table"
                value={String(selectedTable)}
                onChange={(e) =>
                  setSelectedTable(e.target.value === 'none' ? 'none' : Number(e.target.value))
                }
                className="flex-1 rounded-button border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="none">{t.noTable}</option>
                {Array.from({ length: Math.max(1, tableCount) }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {t.table} {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between gap-3">
              <label htmlFor="qr-table-count" className="text-sm font-medium text-primary shrink-0">
                {t.tableCountLabel}
              </label>
              <input
                id="qr-table-count"
                type="number"
                min={1}
                max={200}
                value={tableCount}
                onChange={(e) => {
                  const v = Math.min(200, Math.max(1, Number(e.target.value) || 1))
                  setTableCount(v)
                  if (typeof selectedTable === 'number' && selectedTable > v) setSelectedTable(v)
                }}
                className="w-24 rounded-button border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">{t.tableHelp}</p>
          </div>

          {/* Actions */}
          <button onClick={downloadQR} className="btn btn-primary w-full mb-3" disabled={!imageUrl}>
            {t.download}
          </button>

          <p className="text-xs text-muted-foreground mt-6">{t.place}</p>
        </div>
      </div>
    </ThemeProvider>
  )
}

export default QRPage
